/**
 * BoletosDashboard Component
 * 
 * Dashboard de boletos com estatísticas e visão geral.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  BarChart3, AlertCircle, CheckCircle2, 
  Clock, Download, Plus, FileText, ArrowUpRight
} from 'lucide-react'
import { boletoService } from '../../../../services/erpApi'
import { useToast } from '../../../../hooks/useToast'
import Toast from '../../../../components/Toast/Toast'
import KPICard from '../../../../components/ERP/FinanceDashboard/KPICard/KPICard'
import { formatDateOnly } from '../../../../utils/dateUtils'
import './BoletosDashboard.css'

function BoletosDashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    paidCount: 0,
    waitingCount: 0,
    declinedCount: 0,
    overdueCount: 0,
    totalEmittedMonth: 0,
    totalPaid: 0,
    totalWaiting: 0,
    totalOverdue: 0,
    totalFees: 0,
    totalNet: 0
  })
  const [recentBoletos, setRecentBoletos] = useState([])
  const [loading, setLoading] = useState(true)
  const { toast, showToast, hideToast } = useToast()

  const loadDashboardData = useCallback(async () => {
    setLoading(true)
    try {
      const [statsResponse, boletosResponse] = await Promise.all([
        boletoService.getStats(),
        boletoService.getAll({ limit: 4 })
      ])

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }

      if (boletosResponse.success) {
        setRecentBoletos(boletosResponse.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error)
      showToast('Erro ao carregar dados do dashboard', 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    loadDashboardData()
  }, [loadDashboardData])

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const handleDownloadPdf = async (boleto) => {
    try {
      await boletoService.downloadPdf(boleto.id)
    } catch (error) {
      console.error('Erro ao baixar PDF:', error)
      showToast('Erro ao baixar PDF do boleto', 'error')
    }
  }

  const getStatusConfig = (status) => {
    switch (status) {
      case 'PAID':
        return { label: 'Pago', color: 'paid', icon: CheckCircle2 }
      case 'WAITING':
        return { label: 'Pendente', color: 'waiting', icon: Clock }
      case 'DECLINED':
        return { label: 'Negado', color: 'declined', icon: AlertCircle }
      case 'EXPIRED':
        return { label: 'Vencido', color: 'overdue', icon: AlertCircle }
      default:
        return { label: 'Desconhecido', color: 'unknown', icon: AlertCircle }
    }
  }

  const statsCards = [
    { 
      label: 'Total Emitido (Mês)', 
      value: formatCurrency(stats.totalEmittedMonth), 
      icon: FileText, 
      color: 'text-blue-500',
      bg: 'bg-blue-50',
      change: `${stats.paidCount + stats.waitingCount + stats.declinedCount + stats.overdueCount} boletos`,
      up: true
    },
    { 
      label: 'Boletos Pagos (face)', 
      value: formatCurrency(stats.totalPaid), 
      icon: CheckCircle2, 
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
      change: `${stats.paidCount} boletos`,
      up: true
    },
    { 
      label: 'Taxas de emissão', 
      value: formatCurrency(stats.totalFees), 
      icon: FileText, 
      color: 'text-violet-500',
      bg: 'bg-violet-50',
      change: 'Diferença título × extrato',
      up: false
    },
    { 
      label: 'Líquido creditado', 
      value: formatCurrency(stats.totalNet || Math.max(0, (stats.totalPaid || 0) - (stats.totalFees || 0))), 
      icon: CheckCircle2, 
      color: 'text-teal-500',
      bg: 'bg-teal-50',
      change: 'Após taxas conhecidas',
      up: true
    },
  ]

  const distributionData = [
    { label: 'Pagos', percentage: stats.totalPaid > 0 ? Math.round((stats.totalPaid / (stats.totalPaid + stats.totalWaiting + stats.totalOverdue)) * 100) : 0, color: 'dist-green' },
    { label: 'Pendentes', percentage: stats.totalWaiting > 0 ? Math.round((stats.totalWaiting / (stats.totalPaid + stats.totalWaiting + stats.totalOverdue)) * 100) : 0, color: 'dist-amber' },
    { label: 'Vencidos', percentage: stats.totalOverdue > 0 ? Math.round((stats.totalOverdue / (stats.totalPaid + stats.totalWaiting + stats.totalOverdue)) * 100) : 0, color: 'dist-rose' },
  ]

  return (
    <div className="boletos-dashboard">
      <div className="page-header">
        <div>
          <h1>Dashboard de <span className="text-primary">Boletos</span></h1>
          <p>Gestão e Monitoramento de Cobranças</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary">
            <Download size={16} />
            Exportar Relatório
          </button>
          <button className="btn-primary">
            <Plus size={16} />
            Emitir Novo Boleto
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="stats-grid">
        {statsCards.map((kpi, i) => (
          <KPICard
            key={i}
            label={kpi.label}
            value={kpi.value}
            change={kpi.change}
            up={kpi.up}
            icon={kpi.icon}
            color={kpi.color}
            bg={kpi.bg}
          />
        ))}
      </div>

      <div className="dashboard-grid">
        {/* Main Chart Placeholder */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Fluxo de Recebimentos</h3>
            <div className="chart-trend">
              <ArrowUpRight size={14} />
              <span>+12% vs mês anterior</span>
            </div>
          </div>
          <div className="chart-placeholder">
            <BarChart3 size={48} />
            <p>Gráfico de Evolução Mensal</p>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="distribution-container">
          <h3>Distribuição por Status</h3>
          <div className="distribution-list">
            {distributionData.map((item, idx) => (
              <div key={idx} className="distribution-item">
                <div className="distribution-header">
                  <span className="distribution-label">{item.label}</span>
                  <span className="distribution-percentage">{item.percentage}%</span>
                </div>
                <div className="distribution-bar">
                  <div 
                    className={`distribution-fill ${item.color}`}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="distribution-tip">
            <p>Dica: Aumente a taxa de conversão enviando lembretes automáticos via WhatsApp 2 dias antes do vencimento.</p>
          </div>
        </div>
      </div>

      {/* Recent Activity Table */}
      <div className="recent-table-container">
        <div className="recent-table-header">
          <h3>Últimas Emissões</h3>
          <button 
            className="link-button"
            onClick={() => navigate('/erp/financeiro/boletos')}
          >
            Ver Todos
          </button>
        </div>
        <div className="table-wrapper">
          <table className="recent-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Valor face</th>
                <th>Taxa</th>
                <th>Líquido</th>
                <th>Vencimento</th>
                <th>Status</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="loading-cell">Carregando...</td>
                </tr>
              ) : recentBoletos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-cell">Nenhum boleto encontrado</td>
                </tr>
              ) : (
                recentBoletos.map((boleto) => {
                  const status = getStatusConfig(boleto.status)
                  const StatusIcon = status.icon
                  const fee = boleto.fee_amount != null ? Number(boleto.fee_amount) : null
                  const net =
                    boleto.net_amount != null
                      ? Number(boleto.net_amount)
                      : fee != null
                        ? Math.max(0, Number(boleto.amount || 0) - fee)
                        : null
                  return (
                    <tr key={boleto.id} className="recent-table-row">
                      <td>
                        <div className="recent-client-info">
                          <p className="recent-client-name">{boleto.customer_name || '-'}</p>
                          <span className="recent-boleto-id">
                            ID: {boleto.id ? String(boleto.id).substring(0, 8) : '-'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="recent-value">{formatCurrency(boleto.amount)}</span>
                      </td>
                      <td>
                        <span className="recent-value">{fee != null ? formatCurrency(fee) : '—'}</span>
                      </td>
                      <td>
                        <span className="recent-value">{net != null ? formatCurrency(net) : '—'}</span>
                      </td>
                      <td>
                        <span className="recent-date">{formatDateOnly(boleto.due_date)}</span>
                      </td>
                      <td className="text-center">
                        <span className={`recent-status-badge recent-status-${status.color}`}>
                          <StatusIcon size={12} />
                          {status.label}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="recent-actions">
                          <button 
                            className="recent-action-btn recent-action-download"
                            onClick={() => handleDownloadPdf(boleto)}
                            title="Download PDF"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  )
}

export default BoletosDashboard
